import { UseFormReturn } from 'react-hook-form';
import { CheckboxField, SelectField } from '@brighte/form-components';
import {
  ApplicantType,
  FINANCIAL_PRODUCT,
  RepaymentAccountType,
  RepaymentType,
  repaymentAccountWithTermsSchema,
} from '@brighte/validation';
import { StepNavigator } from '@components/StepNavigator';
import { useApplicationData } from '@provider';
import { Text } from '@spark-web/text';
import { APPLICATION_STEPS, PAYMENT_DELAY_DAYS, REPAYMENT_ACCOUNT, setCheckboxValue } from '@utils';
import CreditCard from './CreditCard';
import DirectDebit from './DirectDebit';
import DirectDebitAgreementButton from './DirectDebitAgreementButton';
import { useRepaymentAccountSummary } from './hooks';

function RepaymentAccount() {
  const {
    TITLE,
    DESCRIPTION,
    TYPE,
    DIRECT_DEBIT_AGREEMENT,
    AUTHORISE_USER,
    REPAYMENT_TYPE,
    SME_AUTHORISE_USER,
    DIRECT_DEBIT_HELPER_TEXT,
  } = REPAYMENT_ACCOUNT;

  const { quote, PaymentDetails: paymentDetails } = useApplicationData(['quote', 'PaymentDetails']);
  const delayDays = PAYMENT_DELAY_DAYS(quote);
  const financialProductId = quote?.financialProduct.id ?? '';
  const authoriseUser = financialProductId === FINANCIAL_PRODUCT.SME ? SME_AUTHORISE_USER : AUTHORISE_USER;

  return (
    <StepNavigator<RepaymentAccountType>
      schema={repaymentAccountWithTermsSchema}
      name={APPLICATION_STEPS.REPAYMENT_ACCOUNT}
      title={TITLE}
      defaultValues={paymentDetails || {}}
      isLoading={false}
      completedContent={useRepaymentAccountSummary()}
    >
      {({ control, setValue, watch, register }: UseFormReturn<RepaymentAccountType>) => {
        const type = watch(`payment.type`);
        if (!type) {
          setValue('payment.type', RepaymentType.DirectDebit);
        }

        return (
          <>
            <Text>{DESCRIPTION(delayDays, financialProductId)}</Text>
            {[FINANCIAL_PRODUCT.BRIGHTE_PAY, FINANCIAL_PRODUCT.SME].includes(financialProductId) ? (
              <>
                <SelectField name="payment.type" control={control} label={REPAYMENT_TYPE} options={TYPE} />
                {type === RepaymentType.DirectDebit && (
                  <Text tone="muted" size="small">
                    {DIRECT_DEBIT_HELPER_TEXT}
                  </Text>
                )}
                {type === RepaymentType.CreditCard ? (
                  <CreditCard control={control} setValue={setValue} />
                ) : (
                  <DirectDebit control={control} setValue={setValue} />
                )}
              </>
            ) : (
              <DirectDebit control={control} setValue={setValue} />
            )}
            <CheckboxField
              name="applicant.termsAndConditions.directDebitStatement"
              control={control}
              onChange={event => setCheckboxValue(event, `${DIRECT_DEBIT_AGREEMENT}`, setValue)}
            >
              <Text>
                {DIRECT_DEBIT_AGREEMENT}{' '}
                <Text inline>
                  <DirectDebitAgreementButton
                    setValue={(value: string) => setValue('applicant.termsAndConditions.directDebitStatement', value)}
                    watch={watch}
                  />
                </Text>
              </Text>
            </CheckboxField>
            <CheckboxField
              name="applicant.termsAndConditions.authoriseBrighteStatement"
              label={authoriseUser}
              control={control}
              onChange={event => setCheckboxValue(event, authoriseUser, setValue)}
            />

            <input type="hidden" {...register('applicant.type')} value={ApplicantType.Primary} />
            <input type="hidden" {...register('payment.methodId')} />
            <input type="hidden" {...register('payment.creditCard.token')} />
            <input type="hidden" {...register('payment.creditCard.cardType')} />
          </>
        );
      }}
    </StepNavigator>
  );
}

export default RepaymentAccount;
